import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ASSET_TYPES, CONDITION_LABELS, CURRENCIES, assetCreatePayload, assetFormProblem, emptyAssetForm, newKey, procurementError,
} from "../lib/procurement";
import { FUNDER_KINDS, FUNDER_LABELS, OWNER_KINDS, OWNER_LABELS, PartyField, usePeople } from "../components/procurement/ProcurementBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NONE = "__none__";

function Text({ id, label, value, onChange, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={`asset-form-${id}`} {...rest} />
    </div>
  );
}

export default function ProcurementAssetForm() {
  const navigate = useNavigate();
  const key = useRef(newKey());
  const { people } = usePeople();
  const [form, setForm] = useState(emptyAssetForm);
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  // A new Idempotency-Key whenever the content changes, so a corrected form is never mistaken for a double tap.
  useEffect(() => { key.current = newKey(); setError(""); }, [form]);
  useEffect(() => { api.get("/procurement/suppliers", { params: { limit: 200 } }).then(({ data }) => setSuppliers(data.items)).catch(() => setSuppliers([])); }, []);

  const problem = assetFormProblem(form);
  const submit = async (event) => {
    event.preventDefault();
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/procurement/assets", assetCreatePayload(form), { headers: { "Idempotency-Key": key.current } });
      toast.success("Actif ajouté à l'inventaire");
      navigate(`/achats/actifs/${data.id}`);
    } catch (e) {
      setError(procurementError(e, "Enregistrement impossible. Réessayez."));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="asset-form-page">
      <div>
        <Link to="/achats/actifs" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Actifs</Link>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1">Nouvel actif</h1>
        <p className="text-sm text-slate-500">Il entre en stock. Vous l'affecterez ensuite à une personne.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4 min-w-0">
          <Card className="p-4 sm:p-5 space-y-4">
            <h2 className="font-semibold text-slate-900">Identification</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(type) => set({ type })}>
                  <SelectTrigger className="h-10" aria-label="Type d'actif" data-testid="asset-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_TYPES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Text id="label" label="Libellé (facultatif)" value={form.label} onChange={(label) => set({ label })} maxLength={120} placeholder="Portable de direction" />
              <Text id="brand" label="Marque" value={form.brand} onChange={(brand) => set({ brand })} maxLength={120} placeholder="Dell" />
              <Text id="model" label="Modèle" value={form.model} onChange={(model) => set({ model })} maxLength={120} placeholder="Latitude 5440" />
              <Text id="serial_number" label="Numéro de série" value={form.serial_number} onChange={(serial_number) => set({ serial_number })} maxLength={120} />
              <div className="space-y-1.5">
                <Label>État</Label>
                <Select value={form.condition} onValueChange={(condition) => set({ condition })}>
                  <SelectTrigger className="h-10" aria-label="État" data-testid="asset-form-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CONDITION_LABELS).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-500">Le numéro de série n'apparaît que sur la fiche de l'actif, jamais dans les listes. Ne saisissez aucun mot de passe ni clé de licence.</p>
          </Card>

          <Card className="p-4 sm:p-5 space-y-4">
            <h2 className="font-semibold text-slate-900">Acquisition</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cost">Coût d'acquisition (facultatif)</Label>
                <div className="flex gap-2">
                  <Input id="cost" inputMode="decimal" value={form.cost} onChange={(e) => set({ cost: e.target.value })} className="h-10" placeholder="450000" data-testid="asset-form-cost" />
                  <Select value={form.currency} onValueChange={(currency) => set({ currency })}>
                    <SelectTrigger className="h-10 w-24" aria-label="Devise" data-testid="asset-form-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="acquired_on">Date d'acquisition</Label>
                <Input id="acquired_on" type="date" value={form.acquired_on} onChange={(e) => set({ acquired_on: e.target.value })} className="h-10" data-testid="asset-form-acquired_on" />
              </div>
              <div className="space-y-1.5">
                <Label>Fournisseur</Label>
                <Select value={form.supplier_id || NONE} onValueChange={(v) => set({ supplier_id: v === NONE ? "" : v })}>
                  <SelectTrigger className="h-10" aria-label="Fournisseur" data-testid="asset-form-supplier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Aucun</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Text id="warranty_until" label="Garantie jusqu'au" type="date" value={form.warranty_until} onChange={(warranty_until) => set({ warranty_until })} />
              <Text id="renewal_on" label="Renouvellement (licence) le" type="date" value={form.renewal_on} onChange={(renewal_on) => set({ renewal_on })} />
              <Text id="location" label="Emplacement" value={form.location} onChange={(location) => set({ location })} maxLength={120} placeholder="Bureau de Dakar" />
            </div>
          </Card>

          <Card className="p-4 sm:p-5 space-y-4">
            <div>
              <h2 className="font-semibold text-slate-900">Qui paie, à qui il appartient</h2>
              <p className="text-sm text-slate-500">Ce sont deux choses différentes : un actif payé par un fondateur n'appartient pas à la société du seul fait de l'achat.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <PartyField id="funder" label="Financeur (qui a payé)" value={form.funder} onChange={(funder) => set({ funder })} kinds={FUNDER_KINDS} kindLabels={FUNDER_LABELS} people={people} />
              <PartyField id="owner" label="Propriétaire juridique" value={form.owner} onChange={(owner) => set({ owner })} kinds={OWNER_KINDS} kindLabels={OWNER_LABELS} people={people}
                hint="« À confirmer » tant que la décision n'est pas prise." />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="note">Note interne (facultative)</Label>
              <Textarea id="note" rows={2} maxLength={1000} value={form.note} onChange={(e) => set({ note: e.target.value })} data-testid="asset-form-note" />
            </div>
          </Card>
        </div>

        <aside className="space-y-4 min-w-0">
          <Card className="p-4 space-y-2 lg:sticky lg:top-20">
            <h2 className="font-semibold text-slate-900">Trois notions, jamais mélangées</h2>
            <dl className="space-y-2 text-sm">
              <div><dt className="font-medium text-slate-800">Financeur</dt><dd className="text-slate-500">Qui a sorti l'argent.</dd></div>
              <div><dt className="font-medium text-slate-800">Propriétaire juridique</dt><dd className="text-slate-500">À qui il appartient. « À confirmer » par défaut.</dd></div>
              <div><dt className="font-medium text-slate-800">Détenteur</dt><dd className="text-slate-500">Qui l'a entre les mains. Il se règle ensuite par une affectation ou un prêt, avec son historique.</dd></div>
            </dl>
            {error && <p className="text-sm text-red-600" role="alert" data-testid="asset-form-error">{error}</p>}
            <div className="flex flex-col gap-2 pt-2">
              <Button type="submit" disabled={busy} data-testid="asset-form-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ajouter l'actif</Button>
              <Button type="button" variant="outline" onClick={() => navigate("/achats/actifs")} disabled={busy}>Annuler</Button>
            </div>
          </Card>
        </aside>
      </div>
    </form>
  );
}
